/**
 * Act 7 — T4 air-cargo scenario. 18 screens: Al Bayt RFQ → Verde Carnes
 * reply → discovery → CIP picker (Verde Carnes arranges · Qatar Airways
 * Cargo) → contract capture → doc carousel (meat-air set) → reconciliation
 * (AWB ↔ Invoice piece-count auto-resolve) → invoice → smart logistics
 * (air modality · VCP → DOH → DXB) → insurance cert → compliance →
 * tariff → LC composer (7-day fast-track) → gate cascade → LC issued
 * (FAB) → tariff final → settlement → T4 reveal.
 */

import type { ReactElement } from "react";
import type { T4Screen } from "@/lib/scenes/screens";
import type { SceneRenderContext } from "@/lib/scenes/act4";

import { AlBaytRfq, VerdeCarnesReply }     from "@/components/scenes/act7/AlBaytRfq";
import { AirCorridorDiscovery }            from "@/components/scenes/act7/AirCorridorDiscovery";
import { LogisticsInsurancePicker }        from "@/components/scenes/shared/LogisticsInsurancePicker";
import { ContractCaptureWithHandshake }    from "@/components/scene/ContractCaptureWithHandshake";
import { DocumentCarousel, MEAT_AIR_DOCS } from "@/components/scenes/act2/DocumentCarousel";
import { ReconciliationMatrix }            from "@/components/scenes/act2/ReconciliationMatrix";
import { InvoiceFsmTile }                  from "@/components/scenes/act2/InvoiceFsmTile";
import { SmartLogistics }                  from "@/components/scenes/act2/SmartLogistics";
import { InsuranceCertAnchor }             from "@/components/scenes/shared/InsuranceCertAnchor";
import { ComplianceSnapshot }              from "@/components/scenes/act2/ComplianceSnapshot";
import { TariffEstimateTile, TariffFinalTile } from "@/components/scenes/shared/TariffTiles";
import { LcComposer }                      from "@/components/scenes/act3/LcComposer";
import { GateCascade }                     from "@/components/scenes/act3/GateCascade";
import { LcIssued }                        from "@/components/scenes/act3/LcIssued";
import { SettlementSimulation }            from "@/components/scenes/act3/SettlementSimulation";
import { T4Reveal }                        from "@/components/scenes/act7/T4Reveal";
import { InvestorEvidence }                from "@/components/scenes/shared/InvestorEvidence";
import { RegulatorEvidence }               from "@/components/scenes/shared/RegulatorEvidence";
import { BankerEvidence }                  from "@/components/scenes/shared/BankerEvidence";
import { LcCompositionProgress }           from "@/components/scenes/shared/LcCompositionProgress";

import { pickInvoiceState, lcGateDecisions, scenarioReputationT4 } from "@/components/scene/SceneRouterHelpers";
import { LcPlacementFeeCta } from "@/components/scenes/act3/LcPlacementFeeCta";
import { USER_ACTION } from "@/lib/userActions/UserActionsContext";

const T4_NOTIONAL_USD = 280_000;

export const ACT7_RENDERERS: Record<T4Screen, (ctx: SceneRenderContext) => ReactElement> = {
  "7.1":  () => <AlBaytRfq />,
  "7.2":  () => <VerdeCarnesReply />,
  "7.3":  () => <AirCorridorDiscovery />,
  "7.4":  () => (
    <LogisticsInsurancePicker
      incoterms="CIP"
      arrangedBy="Verde Carnes"
      lossPayee="FAB"
      cifBasisUsd={T4_NOTIONAL_USD}
      interactiveInsurerPick
      hideCompositionBlock
      insuranceBadgeStyle="mandatory"
      modality="air"
      carriers={[
        { name: "Qatar Airways Cargo · QR8147", modality: "Air · B777F",  etaDays: 1, onTimePct: 96, selected: true },
        { name: "Emirates SkyCargo · EK220",    modality: "Air · B777F",  etaDays: 1, onTimePct: 94, selected: false },
        { name: "Etihad Cargo · EY350",         modality: "Air · A330F",  etaDays: 2, onTimePct: 91, selected: false },
      ]}
      insurers={[
        { name: "Porto Seguro",     policyTemplate: "Air Cargo · Institute Cargo Clauses (Air)",  selected: true },
        { name: "Bradesco Seguros", policyTemplate: "Air Cargo · Institute Cargo Clauses (Air)",  selected: false },
        { name: "AXA XL Brasil",    policyTemplate: "Air Cargo · Institute Cargo Clauses (Air B)", selected: false },
      ]}
    />
  ),
  "7.5": ({ ev, signParty }) => (
    <ContractCaptureWithHandshake
      ev={ev}
      signParty={signParty}
      requireSignCta
      showLcCompositionTrigger
      signCtaActionKey={USER_ACTION.T4_SIGN_CONTRACT}
      lcCompositionActionKey={USER_ACTION.T4_BEGIN_LC_COMPOSITION}
    />
  ),
  "7.6": ({ ev, graph }) => (
    <DocumentCarousel
      documents={ev.documents}
      graph={graph}
      docs={MEAT_AIR_DOCS}
      carouselSubtitle="Document carousel · 6 air-cargo meat tiles · CDIAL Halal + MAPA + ABCC chain"
      headerAttestor={{
        src: "/attestors/orbid-abcc-cert-br.svg",
        alt: "ABCC",
        caption: "Origination attested · Arab-Brazilian Chamber of Commerce (ABCC)",
      }}
    />
  ),
  "7.7": ({ ev }) => <ReconciliationMatrix reconciliation={ev.reconciliation} showAwbDescError />,
  "7.8": ({ ev, signParty, invoiceSignerHeader }) => (
    <InvoiceFsmTile
      invoiceState={pickInvoiceState(ev, "INV-2026-0481")}
      invoiceId="INV-2026-0481"
      einvoiceSigner={signParty.einvoice}
      einvoicePdf={signParty.einvoicePdf}
      invoiceSignerHeader={invoiceSignerHeader}
      requireSignCta
      ctaActionKey={USER_ACTION.T4_SIGN_INVOICE}
    />
  ),
  "7.9": ({ ev }) => (
    <SmartLogistics
      milestones={ev.logisticsMilestones}
      direction="br-to-ae"
      modality="air"
      carrierName="✈ Qatar Airways Cargo · MAWB 157-VCP-DXB"
    />
  ),
  "7.10": ({ ev }) => (
    <InsuranceCertAnchor
      shipmentLoaded={ev.logisticsMilestones.some((m) => m.milestone === "Loading")}
      policyType="Air cargo · Institute Cargo Clauses (Air)"
      coverage="≥ 110% invoice · USD 308,000"
      insurerName="Porto Seguro"
      lossPayee="FAB"
      arrangedBy="Verde Carnes"
      mandatoryUcp28
      viewCertificatePdf="/sign-docs/insurance-t4.pdf"
      viewCertificateRef="POL-2026-0481 · 110% invoice · ICC(Air)"
    />
  ),
  "7.11": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <ComplianceSnapshot
          snapshot={ev.complianceSnapshot}
          expectedCorridorLabel={"Corridor Portal Único Siscomex — Dubai Cargo (DXB)\nBrazil (origin) → UAE (destination) · Air"}
          expectedAuthorities={["Receita Federal (BRL)", "E-invoicing and VAT (UAE)", "DXB Cargo Customs", "MOCCAE Porto", "OFAC + UAE Sanctions (Internal Use Only)", "Compliance Hard Stops (Internal Use Only)", "TravelRule (Internal Use Only)"]}
        />
      </div>
      <InvestorEvidence variant="trade-body" notionalUsd={T4_NOTIONAL_USD} stageLabel="Compliance gate" />
    </div>
  ),
  "7.12": () => (
    <div className="flex h-full flex-col gap-3">
      <TariffEstimateTile cifUsd={T4_NOTIONAL_USD} jurisdiction="UAE" hsCode="0201.30" />
      <LcCompositionProgress
        startPct={15}
        targetPct={50}
        label="Integrating estimated Tariff into LC composition"
      />
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-orbitra-muted">
        Tariff estimate at LC composition · UAE import
      </p>
      <div className="flex-1 min-h-0" />
    </div>
  ),
  "7.13": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <LcCompositionProgress
        startPct={50}
        targetPct={85}
        label="Integrating contract and invoice data into LC composition"
      />
      <div className="flex-1 min-h-0">
        <LcComposer contract={ev.contract} composer={ev.lcComposer} lcMode selectedTenorDays={7} />
      </div>
      <BankerEvidence variant="lc-composer" amountUsd={T4_NOTIONAL_USD} bankBadge="FAB" />
    </div>
  ),
  "7.14": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <GateCascade decisions={lcGateDecisions(ev, "t4-air-cargo")} settlementRef="LC-2026-00481" />
      </div>
      <BankerEvidence variant="gate-cascade" amountUsd={T4_NOTIONAL_USD} bankBadge="FAB" />
      <LcPlacementFeeCta
        feeUsd={850}
        bankBadge="FAB"
        actionKey={USER_ACTION.T4_PAY_LC_PLACEMENT_FEE}
      />
    </div>
  ),
  "7.15": ({ ev, graph }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <LcIssued
          lc={ev.lcIssuance}
          buyerLabel="Al Bayt · Importer"
          sellerLabel="Verde Carnes · Exporter"
          graph={graph}
          showDigitalLcModal
          digitalLcAutoOpenKey={USER_ACTION.T4_VIEW_LC_ISSUED}
          presentationPeriodDays={7}
        />
      </div>
      <RegulatorEvidence variant="lc-issued" lcReference={ev.lcIssuance?.lcReference} />
      <InvestorEvidence variant="trade-body" notionalUsd={T4_NOTIONAL_USD} stageLabel="LC issued · fast-track" />
    </div>
  ),
  "7.16": () => (
    <div className="grid h-full place-items-center">
      <div className="w-full max-w-2xl">
        <TariffFinalTile cifUsd={T4_NOTIONAL_USD} jurisdiction="UAE" hsCode="0201.30" settled />
      </div>
    </div>
  ),
  "7.17": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <SettlementSimulation
          legs={ev.settlementLegs}
          threeFieldMatched={ev.threeFieldMatched}
          invoiceMatched={ev.invoiceState["INV-2026-0481"] === "MATCHED"}
        />
      </div>
      <BankerEvidence variant="settlement" amountUsd={T4_NOTIONAL_USD} bankBadge="FAB" />
      <RegulatorEvidence variant="settlement" />
      <InvestorEvidence variant="trade-body" notionalUsd={T4_NOTIONAL_USD} stageLabel="Settlement" />
    </div>
  ),
  "7.18": ({ ev }) => {
    const t4 = scenarioReputationT4(ev);
    return (
      <div className="flex h-full flex-col gap-3">
        <div className="flex-1 min-h-0">
          <T4Reveal verdeCarnes={t4.verdeCarnes} alBayt={t4.alBayt} />
        </div>
        <InvestorEvidence variant="reveal" notionalUsd={T4_NOTIONAL_USD} />
      </div>
    );
  },
};
